import { createFileRoute, Link } from "@tanstack/react-router";
import { hooks as h } from "@/hooks";
import { ui } from "@patient-watch/ui";
import { Badge } from "@patient-watch/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@patient-watch/ui/table";
import { Loader2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useOptionIndex } from "@/features/dictionaries/api/dictionaryBuilderApi";

const PAGE_SIZE = 100;

const formatValue = (value: unknown) =>
    value == null ? "" : typeof value === "string" ? value : JSON.stringify(value);

/** Every answer option and where it lives. Options are edited where they live: a dictionary or a form question. */
const OptionsBuilder = () => {
    const { data: options, isLoading } = useOptionIndex();
    const [query, setQuery] = useState("");
    const [limit, setLimit] = useState(PAGE_SIZE);

    h.useNav([
        { label: "Builder", route: { to: "/builder" } },
        { label: "Options", route: { to: "/builder/options" } },
    ]);

    const filtered = useMemo(() => {
        const needle = query.trim().toLowerCase();
        return (options ?? []).filter((option) =>
            [
                option.label,
                formatValue(option.value),
                option.dictionary_name,
                option.dictionary_display_name,
                option.form_name,
                option.group_label,
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase()
                .includes(needle)
        );
    }, [options, query]);

    return (
        <div className="space-y-6 p-2 md:p-4">
            <div>
                <h1 className="font-semibold text-3xl tracking-tight">Options</h1>
                <p className="text-muted-foreground">
                    Every answer option, and where it lives. Edit shared lists in Dictionaries, and a single
                    question&apos;s own options in the form builder.
                </p>
            </div>

            <div className="flex items-center gap-3">
                <ui.Input
                    value={query}
                    onChange={(event) => {
                        setQuery(event.target.value);
                        setLimit(PAGE_SIZE);
                    }}
                    placeholder="Search options, dictionaries or forms..."
                    className="max-w-sm"
                />
                <span className="text-muted-foreground text-sm tabular-nums">{filtered.length} options</span>
            </div>

            <ui.Card>
                {isLoading ? (
                    <div className="flex justify-center items-center py-12">
                        <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" />
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Label</TableHead>
                                <TableHead>Value</TableHead>
                                <TableHead>Dictionary / question</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filtered.slice(0, limit).map((option) => (
                                <TableRow key={option.option_id} className={option.is_disabled ? "opacity-60" : ""}>
                                    <TableCell className="font-medium">
                                        {option.label}
                                        {option.is_disabled ? (
                                            <Badge variant="outline" className="ml-2 text-2xs">
                                                Hidden
                                            </Badge>
                                        ) : null}
                                        {option.group_label ? (
                                            <div className="font-normal text-muted-foreground text-xs">
                                                {option.group_label}
                                            </div>
                                        ) : null}
                                    </TableCell>
                                    <TableCell className="font-mono text-xs">{formatValue(option.value)}</TableCell>
                                    <TableCell className="text-sm">
                                        {option.dictionary_id ? (
                                            <Link
                                                to="/builder/dictionaries/$dictionaryId"
                                                params={{ dictionaryId: option.dictionary_id }}
                                                className="hover:underline"
                                            >
                                                {option.dictionary_display_name}
                                            </Link>
                                        ) : option.form_id && option.input_id ? (
                                            <Link
                                                to="/builder/forms/$formId/$tab"
                                                params={{ formId: option.form_id, tab: "questions" }}
                                                search={{ inputId: option.input_id }}
                                                className="hover:underline"
                                            >
                                                {option.form_name}: {option.input_label}
                                            </Link>
                                        ) : (
                                            <span className="text-muted-foreground">Not linked</span>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </ui.Card>
            {filtered.length > limit ? (
                <div className="flex justify-center">
                    <ui.Button variant="outline" size="sm" onClick={() => setLimit((current) => current + PAGE_SIZE)}>
                        Show more
                    </ui.Button>
                </div>
            ) : null}
        </div>
    );
};

export const Route = createFileRoute("/_app/builder/options/")({
    component: OptionsBuilder,
});
